import React, { useState, useEffect } from 'react';
import { FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';

import Table from '../../../components/admin/common/Table';
import Button from '../../../components/admin/common/Button';
import Modal from '../../../components/admin/common/Modal';
import { getTags, createTag, updateTag, deleteTag } from '../../../services/tagService';

const TagsPage = () => {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Fetch tags
  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const response = await getTags();
      setTags(response.data || []);
      setError(null);
    } catch (err) {
      setError(err.toString());
      console.error('Error fetching tags:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Open modal for creating a new tag
  const handleAddClick = () => {
    setSelectedTag(null);
    setFormData({ name: '', description: '' });
    setModalOpen(true);
  };

  // Open modal for editing existing tag
  const handleEditClick = (tag, e) => {
    if (e) e.stopPropagation();
    setSelectedTag(tag);
    setFormData({
      name: tag.name,
      description: tag.description || '',
    });
    setModalOpen(true);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (tag, e) => {
    if (e) e.stopPropagation();
    setSelectedTag(tag);
    setDeleteModalOpen(true);
  };

  // Save tag (create or update)
  const handleSaveTag = async () => {
    try {
      if (selectedTag) {
        // Update existing tag
        await updateTag(selectedTag._id, formData);
      } else {
        // Create new tag
        await createTag(formData);
      }
      
      // Refresh tags list
      fetchTags();
      setModalOpen(false);
    } catch (err) {
      setError(err.toString());
      console.error('Error saving tag:', err);
    }
  };

  // Delete tag
  const handleDeleteTag = async () => {
    try {
      await deleteTag(selectedTag._id);
      fetchTags();
      setDeleteModalOpen(false);
    } catch (err) {
      setError(err.toString());
      console.error('Error deleting tag:', err);
    }
  };

  // Table columns definition
  const columns = [
    {
      key: 'name',
      header: 'Name',
      width: '30%'
    },
    {
      key: 'description',
      header: 'Description',
      render: (tag) => tag.description || '-'
    },
    {
      key: 'products',
      header: 'Products',
      render: (tag) => tag.products || '0'
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '100px',
      render: (tag) => (
        <div className="flex space-x-2">
          <button 
            onClick={(e) => handleEditClick(tag, e)}
            className="text-blue-600 hover:text-blue-800"
            title="Edit"
          >
            <FiEdit size={18} />
          </button>
          <button 
            onClick={(e) => handleDeleteClick(tag, e)}
            className="text-red-600 hover:text-red-800"
            title="Delete"
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Tags</h1>
        <Button 
          variant="primary" 
          onClick={handleAddClick}
          icon={<FiPlus />}
        >
          Add Tag
        </Button>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
          Error: {error}
        </div>
      )}

      <Table 
        columns={columns} 
        data={tags} 
        isLoading={isLoading}
        emptyMessage="No tags found. Add your first tag!"
      />

      {/* Add/Edit Tag Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={selectedTag ? 'Edit Tag' : 'Add Tag'}
        size="md"
        footer={
          <>
            <Button 
              variant="outline" 
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveTag}
            >
              {selectedTag ? 'Save Changes' : 'Create Tag'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name*
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded h-24"
            ></textarea>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Tag"
        size="sm"
        footer={
          <>
            <Button 
              variant="outline" 
              onClick={() => setDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDeleteTag}
            >
              Delete
            </Button>
          </>
        }
      >
        <p>
          Are you sure you want to delete <strong>{selectedTag?.name}</strong>?
        </p>
        {selectedTag?.products > 0 && (
          <p className="mt-2 text-red-600">
            Warning: This tag is used by {selectedTag.products} products.
            The tag will be removed from those products if deleted.
          </p>
        )}
      </Modal>
    </div>
  );
};

export default TagsPage; 